import { LitElement, html, css } from 'lit'
import { navigate } from '../router.js'
import { api } from '../api.js'
import { toast } from './app-toast.js'
import { openPicker } from '../wmj-flow.js'
import { formatDate } from '../status.js'

// One Pending-Triage ticket (§7.6). A ticket leaves triage only once a Workamajig
// company + job and an owner are all chosen — company/job use the same picker modal
// as the ticket sidebar; assignee is an agent typeahead. Selections are collected
// locally and submitted in one call; on success `resolved` fires so the queue drops
// the row. (Group assignment slots in with Phase 4.)
class TriageRow extends LitElement {
  static properties = {
    ticket: { type: Object },
    _company: { state: true },   // { clientId, name }
    _job: { state: true },       // { jobNumber, name }
    _task: { state: true },      // { taskId, name } — the budget line (§19.1)
    _assignee: { state: true },  // { id, name }
    _assigneeQuery: { state: true },
    _assigneeResults: { state: true },
    _group: { state: true },     // groupId (owner = an agent OR a group, not both)
    _groups: { state: true },
    _blockDomain: { state: true },  // widen the block from the sender to their whole domain
    _busy: { state: true }
  }

  static styles = css`
    :host { display: block; font-family: var(--font-sans); }
    .card { display: flex; align-items: stretch; overflow: hidden; background: var(--white);
            border-radius: var(--radius-card); box-shadow: var(--shadow-row); }
    .spine { width: 4px; flex-shrink: 0; background: var(--chartreuse-500); }
    .row { flex: 1; display: grid; grid-template-columns: 1fr 360px; gap: 32px; padding: 24px 28px; align-items: start; }
    @media (max-width: 820px) { .row { grid-template-columns: 1fr; gap: 20px; } }

    .id { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; }
    .num { font-family: var(--font-eyebrow); font-size: 13px; font-weight: 500; color: var(--neutral-500); font-variant-numeric: tabular-nums; }
    .tag { font-family: var(--font-eyebrow); font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
           color: var(--chartreuse-700); background: var(--chartreuse-bg); padding: 3px 9px; border-radius: 999px; }
    .subject { font-size: 20px; font-weight: 700; letter-spacing: -0.01em; color: var(--eggplant-500); line-height: 1.25;
               display: inline-block; cursor: pointer; }
    .subject:hover { color: var(--sky-500); }
    .from { font-size: 14px; color: var(--neutral-600); margin-top: 8px; }
    .from .addr { color: var(--sky-500); font-weight: 600; }
    .received { font-size: 13px; color: var(--neutral-500); margin-top: 3px; }

    .resolve { display: grid; gap: 16px; }
    .pair { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .label { font-family: var(--font-eyebrow); font-size: 10.5px; font-weight: 700; letter-spacing: 0.1em;
             text-transform: uppercase; color: var(--label-grey); margin-bottom: 7px; }
    .picker { cursor: pointer; box-sizing: border-box; height: 44px; display: flex; align-items: center;
              font-family: var(--font-sans); font-size: 15px; color: var(--eggplant-500); padding: 0 14px;
              background: var(--white); box-shadow: inset 0 0 0 1px var(--eggplant-500); }
    .picker:hover { box-shadow: inset 0 0 0 2px var(--eggplant-500); }
    .picker.unset { color: #B4232B; box-shadow: inset 0 0 0 1px #B4232B; }
    .picker.muted { color: var(--neutral-500); }
    input, select { width: 100%; box-sizing: border-box; height: 44px; font-family: var(--font-sans); font-size: 15px;
            padding: 0 14px; background: var(--white); border: none; border-radius: 0; color: var(--eggplant-500); outline: none; }
    input { box-shadow: inset 0 0 0 2px var(--border-input); }
    input:focus { box-shadow: inset 0 0 0 2px var(--eggplant-500); }
    select { box-shadow: inset 0 0 0 1px var(--eggplant-500); appearance: none; -webkit-appearance: none; padding-right: 34px; cursor: pointer;
             background-image: linear-gradient(45deg, transparent 50%, var(--eggplant-500) 50%), linear-gradient(135deg, var(--eggplant-500) 50%, transparent 50%);
             background-position: right 16px center, right 11px center; background-size: 5px 5px, 5px 5px; background-repeat: no-repeat; }
    .results { box-shadow: inset 0 0 0 1px var(--border-input); margin-top: 4px; }
    .results div { padding: 8px 12px; cursor: pointer; font-size: 14px; color: var(--eggplant-500); }
    .results div:hover { background: var(--neutral-50); }
    /* The routine actions sit together on the left; SPAM is pushed to the far right with
       a wide gap between them, because it force-closes a ticket AND blocks the sender —
       a misclick that lands on it is not one somebody notices. */
    .actions { display: flex; justify-content: space-between; align-items: flex-end; gap: 56px; margin-top: 2px; }
    @media (max-width: 820px) { .actions { gap: 32px; } }
    .actions .routine { display: flex; gap: 12px; }
    .danger-zone { display: flex; flex-direction: column; align-items: flex-end; gap: 7px; }
    button.cta, button.outline { display: inline-flex; align-items: center; gap: 8px; cursor: pointer;
             font-family: var(--font-sans); font-weight: 600; font-size: 16px; line-height: 24px;
             padding: 12px 24px; border: none; border-radius: 0; transition: background-color .18s ease, color .18s ease; }
    button.cta { background: var(--chartreuse-500); color: var(--eggplant-500); }
    button.cta:hover { background: var(--chartreuse-600); }
    button.outline { background: transparent; color: var(--eggplant-500); box-shadow: inset 0 0 0 1px var(--eggplant-500); }
    button.outline:hover { background: var(--eggplant-500); color: var(--neutral-50); }
    button.danger { background: #B4232B; color: var(--white); letter-spacing: 0.06em;
             font-family: var(--font-eyebrow); font-weight: 700; font-size: 13px; text-transform: uppercase; }
    button.danger:hover { background: #8E1B22; }
    .block-domain { display: flex; align-items: center; gap: 7px; font-size: 12.5px; color: var(--neutral-600);
             cursor: pointer; user-select: none; white-space: nowrap; }
    .block-domain input { width: 14px; height: 14px; accent-color: #B4232B; cursor: pointer; }
    button:disabled { opacity: 0.4; cursor: not-allowed; }
    .arrow { font-size: 16px; line-height: 0; }
  `

  constructor() {
    super()
    this._company = null
    this._job = null
    this._task = null
    this._assignee = null
    this._assigneeQuery = ''
    this._assigneeResults = []
    this._group = ''
    this._groups = []
    this._blockDomain = false
    this._busy = false
  }

  connectedCallback() {
    super.connectedCallback()
    api.get('/groups').then(r => { this._groups = r.groups }).catch(() => {})
  }

  // Owner is at most one of an agent or a group: picking a group clears the agent.
  _setGroup(e) {
    this._group = e.target.value || ''
    if (this._group) { this._assignee = null; this._assigneeQuery = ''; this._assigneeResults = [] }
  }

  async _pickCompany() {
    const company = await openPicker({ mode: 'company' })
    if (!company) return
    this._company = { clientId: company.clientId, name: company.name }
    this._job = null   // job is scoped to the company; re-pick for the new one
    this._task = null
  }

  async _pickTask() {
    if (!this._job) return
    const task = await openPicker({ mode: 'task', jobNumber: this._job.jobNumber })
    if (!task) return
    this._task = { taskId: task.taskId, name: task.name }
  }

  async _pickJob() {
    if (!this._company) { toast('Select a company first', 'info'); return }
    const job = await openPicker({ mode: 'job', clientId: this._company.clientId })
    if (!job) return
    this._job = { jobNumber: job.jobNumber, name: job.name }
    this._task = null   // a task belongs to one job; re-pick for the new one
  }

  async _searchAssignee(e) {
    this._assigneeQuery = e.target.value
    if (this._assigneeQuery.trim().length < 2) { this._assigneeResults = []; return }
    try { this._assigneeResults = await api.get(`/users?query=${encodeURIComponent(this._assigneeQuery.trim())}&role=agent`) }
    catch { this._assigneeResults = [] }
  }

  _selectAssignee(u) {
    this._assignee = { id: u.id, name: u.fullName || u.email }
    this._assigneeQuery = ''
    this._assigneeResults = []
    this._group = ''   // an individual owner and a group are mutually exclusive
  }

  get _canSubmit() { return !!this._company }

  _emitResolved() {
    this.dispatchEvent(new CustomEvent('resolved', { detail: { number: this.ticket.number }, bubbles: true, composed: true }))
  }

  // Submit needs only a client; job + owner are optional and sent when chosen.
  async _submit() {
    if (!this._canSubmit || this._busy) return
    this._busy = true
    try {
      await api.post(`/tickets/${this.ticket.number}/triage`, {
        workamajigCompanyId: this._company.clientId,
        ...(this._job ? { workamajigJobNumber: this._job.jobNumber } : {}),
        ...(this._task ? { workamajigTaskId: this._task.taskId } : {}),
        ...(this._assignee ? { assigneeId: this._assignee.id } : {}),
        ...(this._group ? { groupId: this._group } : {})
      })
      toast(`Ticket #${this.ticket.number} submitted`, 'success')
      this._emitResolved()
    } catch (err) {
      toast(err.message || 'Failed to submit the ticket', 'error')
    } finally {
      this._busy = false
    }
  }

  // The sender's domain, for the "block everyone at it" option and the confirm text.
  get _senderDomain() {
    return (this.ticket?.requester?.email || '').split('@')[1] || null
  }

  // Close — straight to closed, no fields required, for a ticket that needs no routing
  // and is not spam. Confirmed first, since a stray click would close a real ticket.
  async _close() {
    if (this._busy) return
    if (!confirm(`Close ticket #${this.ticket.number}? This moves it straight to closed.`)) return
    this._busy = true
    try {
      await api.post(`/tickets/${this.ticket.number}/triage/close`)
      toast(`Ticket #${this.ticket.number} closed`, 'success')
      this._emitResolved()
    } catch (err) {
      toast(err.message || 'Failed to close the ticket', 'error')
    } finally {
      this._busy = false
    }
  }

  // SPAM — closes this ticket AND blocks the sender, so their next email is filed the same
  // way without anybody reading it. The confirm spells out both halves and names exactly
  // what is about to be blocked: the second half is the one somebody cannot see happening.
  async _spam() {
    if (this._busy) return
    const from = this.ticket.requester?.email || 'this sender'
    const scope = this._blockDomain && this._senderDomain
      ? `everyone at @${this._senderDomain}`
      : from
    if (!confirm(
      `Mark ticket #${this.ticket.number} as SPAM?\n\n` +
      `It will be closed, and future email from ${scope} will be filed as spam automatically ` +
      `without reaching anyone. You can undo this on the ticket.`
    )) return
    this._busy = true
    try {
      const res = await api.post(`/tickets/${this.ticket.number}/spam`, { blockDomain: this._blockDomain })
      // The server refuses to block our own people or a client's domain, and says so rather
      // than failing the call — the ticket still closed, so the agent has to be told which
      // half happened.
      if (res?.refused?.length) toast(`Marked as spam, but nothing was blocked: ${res.refused.join('; ')}`, 'info')
      else toast(`Ticket #${this.ticket.number} marked as spam — blocked ${res.blocked.join(' and ')}`, 'success')
      this._emitResolved()
    } catch (err) {
      toast(err.message || 'Failed to mark the ticket as spam', 'error')
    } finally {
      this._busy = false
    }
  }

  render() {
    const t = this.ticket
    if (!t) return html``
    const from = t.requester?.email || t.requester?.name
    return html`
      <div class="card">
        <div class="spine"></div>
        <div class="row">
          <div style="min-width:0">
            <div class="id">
              <span class="num">#${t.number}</span>
              <span class="tag">Unrecognized domain</span>
            </div>
            <a class="subject" @click=${() => navigate(`/tickets/${t.number}`)}>${t.subject}</a>
            ${from ? html`<div class="from">From <span class="addr">${from}</span></div>` : ''}
            <div class="received">Received ${formatDate(t.openDate)}</div>
          </div>
          <div class="resolve">
            <div>
              <div class="label">Client</div>
              <div class="picker ${this._company ? '' : 'unset'}" @click=${this._pickCompany}>
                ${this._company ? this._company.name : 'Select company…'}
              </div>
            </div>
            <div>
              <div class="label">Workamajig job (optional)</div>
              <div class="picker ${this._job ? '' : 'muted'}" @click=${this._pickJob}>
                ${this._job ? `#${this._job.jobNumber}` : 'Select job…'}
              </div>
            </div>
            <div>
              <div class="label">Workamajig task (optional)</div>
              <div class="picker ${this._task ? '' : 'muted'}" @click=${this._pickTask}>
                ${this._task ? this._task.name : this._job ? 'Select task…' : 'Pick a job first'}
              </div>
            </div>
            <div class="pair">
              <div>
                <div class="label">Owner — agent</div>
                ${this._assignee
                  ? html`<div class="picker" @click=${() => (this._assignee = null)}>${this._assignee.name}</div>`
                  : html`
                    <input placeholder="Assign agent…"
                      .value=${this._assigneeQuery} @input=${this._searchAssignee} />
                    ${this._assigneeResults.length ? html`<div class="results">
                      ${this._assigneeResults.map(u => html`<div @click=${() => this._selectAssignee(u)}>${u.fullName || u.email}</div>`)}
                    </div>` : ''}`}
              </div>
              <div>
                <div class="label">Owner — group</div>
                <select @change=${this._setGroup} .value=${this._group}>
                  <option value="">No group</option>
                  ${this._groups.map(g => html`<option value=${g.id} ?selected=${this._group === g.id}>${g.name}</option>`)}
                </select>
              </div>
            </div>
            <div class="actions">
              <div class="routine">
                <button class="cta" ?disabled=${!this._canSubmit || this._busy} @click=${this._submit}>
                  Route ticket <span class="arrow">→</span>
                </button>
                <button class="outline" ?disabled=${this._busy} @click=${this._close}>Close</button>
              </div>
              <div class="danger-zone">
                ${this._senderDomain ? html`
                  <label class="block-domain">
                    <input type="checkbox" .checked=${this._blockDomain}
                      @change=${e => (this._blockDomain = e.target.checked)} />
                    Block all @${this._senderDomain}
                  </label>` : ''}
                <button class="danger" ?disabled=${this._busy} @click=${this._spam}>Spam</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `
  }
}

customElements.define('triage-row', TriageRow)
